export const APP_TIME_ZONE = "America/Fortaleza";

const shortMonthMap: Record<string, number> = {
  jan: 1,
  fev: 2,
  mar: 3,
  abr: 4,
  mai: 5,
  jun: 6,
  jul: 7,
  ago: 8,
  set: 9,
  out: 10,
  nov: 11,
  dez: 12,
};

const displayDateFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: APP_TIME_ZONE,
});

const timeZonePartsFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: APP_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function getTimeZoneParts(date: Date) {
  const parts = timeZonePartsFormatter.formatToParts(date);
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
}

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function parsePtBrShortDate(value: string) {
  const normalized = value
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  const match = normalized.match(/^(\d{1,2})\s+([a-z]{3})\s+(\d{4})$/);
  if (!match) {
    return null;
  }

  const day = Number(match[1]);
  const month = shortMonthMap[match[2]];
  const year = Number(match[3]);

  if (!month || day < 1 || day > 31) {
    return null;
  }

  return { day, month, year };
}

function parseCanonicalDate(value: string) {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  return { year, month, day };
}

function createStableDate(year: number, month: number, day: number) {
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
}

export function getMillisecondsUntilEndOfDayInAppTimeZone(now: Date = new Date()) {
  const { hour, minute, second } = getTimeZoneParts(now);
  const remainingSeconds = ((23 - hour) * 60 + (59 - minute)) * 60 + (59 - second);
  return remainingSeconds * 1000 + (999 - now.getMilliseconds());
}

export function toCanonicalDateFromPtBr(value: string) {
  const parsed = parsePtBrShortDate(value);
  if (!parsed) {
    return value;
  }

  return `${parsed.year}-${pad(parsed.month)}-${pad(parsed.day)}`;
}

export function formatCanonicalDateForDisplay(value: string) {
  const parsed = parseCanonicalDate(value);
  if (!parsed) {
    return value;
  }

  return displayDateFormatter.format(createStableDate(parsed.year, parsed.month, parsed.day));
}

export function getSortableTimestampFromCanonicalDate(value: string) {
  const parsed = parseCanonicalDate(value);
  if (!parsed) {
    return 0;
  }

  return Date.UTC(parsed.year, parsed.month - 1, parsed.day);
}

export function formatCanonicalDateForStructuredData(value: string) {
  const parsed = parseCanonicalDate(value);
  if (!parsed) {
    return value;
  }

  return `${value}T00:00:00-03:00`;
}
